import { CSSProperties } from 'react';
import { useIntl } from 'react-intl';
import { FarmSocialPost } from 'types/SocialNetwork';
import { imgSrcParser } from 'utils/imgSrcParser';

const MIN_ITEMS_PER_GROUP = 8;
const SECONDS_PER_ITEM = 5;

interface FarmPhotoStripProps {
  posts?: FarmSocialPost[];
}

const FarmPhotoStrip = ({ posts }: FarmPhotoStripProps) => {
  const intl = useIntl();

  if (!posts?.length) return null;

  const rounds = Math.ceil(MIN_ITEMS_PER_GROUP / posts.length);
  const items = Array.from({ length: rounds }, (_, round) =>
    posts.map((post) => ({ post, round }))
  ).flat();
  const linkLabel = intl.formatMessage({ id: 'farm_view.open_social_post' });

  const renderGroup = (isClone: boolean) => (
    <div
      className={`farm-photo-strip-group${isClone ? ' is-clone' : ''}`}
      aria-hidden={isClone || undefined}
    >
      {items.map(({ post, round }) => {
        const isRepeat = isClone || round > 0;
        const image = (
          <img src={imgSrcParser(post.image)} alt={''} loading={'lazy'} />
        );

        return post.permalink ? (
          <a
            key={`${post.id}-${round}`}
            className={'farm-photo-strip-item'}
            href={post.permalink}
            target={'_blank'}
            rel={'noopener noreferrer'}
            aria-label={linkLabel}
            aria-hidden={isRepeat || undefined}
            tabIndex={isRepeat ? -1 : undefined}
          >
            {image}
          </a>
        ) : (
          <span key={`${post.id}-${round}`} className={'farm-photo-strip-item'}>
            {image}
          </span>
        );
      })}
    </div>
  );

  return (
    <section
      className={'farm-photo-strip'}
      aria-label={intl.formatMessage({ id: 'farm_view.social_photos' })}
      style={
        {
          '--farm-photo-strip-duration': `${items.length * SECONDS_PER_ITEM}s`,
        } as CSSProperties
      }
    >
      <div className={'farm-photo-strip-track'}>
        {renderGroup(false)}
        {renderGroup(true)}
      </div>
    </section>
  );
};

export default FarmPhotoStrip;
